import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import EmailValidator from 'email-validator';
import Mailcheck from 'mailcheck';
import {
  asUrlParams, ALLOWED_STUDENT_EMAIL_DOMAIN_REGEX, BTS_ACCOUNT_SIGNUP,
} from './utils';
import i18n from './i18n';

Mailcheck.defaultDomains.push(
  'temple.edu',
  'bicycletransit.com',
  'sas.upenn.edu',
  'drexel.edu',
  'wharton.upenn.edu',
  'jefferson.edu',
  'qq.com',
  'udel.edu',
  'gmx.de',
  'seas.upenn.edu',
  'rocketmail.com',
  'cornell.edu',
  'web.de',
  'villanova.edu',
  'lacity.org',
  'usc.edu',
  'sbcglobal.net',
  'metro.net',
  'ladwp.com',
  'dot.ca.gov',
  'naver.com',
  'caltech.edu',
  'google.com',
  'ucla.edu',
  'hey.com',
  'afi.com',
);

Vue.use(Vuex);

const DEFAULT_USER = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  password: '',
  addressStreet: '',
  addressStreet2: '',
  addressCity: '',
  addressState: BTS_ACCOUNT_SIGNUP.default_state,
  zipCode: '',
  countryId: 236,
  optInEmail: true,
  optInSms: true,
  region: null,
};

const DEFAULT_REGISTRATION = {
  stripeToken: null,
  planId: null,
  fullfillCard: false,
  paymentMethod: 'credit',
  accessCard: null,
  tapCard: null,
  rfidCard: null,
  enrollmentCode: null,
  discountVerificationNonce: null,
  discountEligibilityType: null,
};

export default new Vuex.Store({
  state: {
    nonce: BTS_ACCOUNT_SIGNUP.nonce,
    apiUrl: BTS_ACCOUNT_SIGNUP.api_url,
    stripeKey: BTS_ACCOUNT_SIGNUP.stripe_pub_key,
    enableSms: BTS_ACCOUNT_SIGNUP.enable_sms,
    requireAddress: BTS_ACCOUNT_SIGNUP.require_address,
    systemId: BTS_ACCOUNT_SIGNUP.system_id,
    supportsPayButton: false,
    delayPrepaidCardValidation: BTS_ACCOUNT_SIGNUP.delay_prepaid_card_validation,
    registration: JSON.parse(JSON.stringify(DEFAULT_REGISTRATION)),
    promotionCode: {},
    emailValidation: {},
    phoneVerification: {
      requested: {},
      confirmed: {},
    },
    user: JSON.parse(JSON.stringify(DEFAULT_USER)),
    order: {
      complete: false,
    },
    orderCompleteHTML: BTS_ACCOUNT_SIGNUP.order_complete_html,
    passTypes: BTS_ACCOUNT_SIGNUP.pass_types,
    regions: BTS_ACCOUNT_SIGNUP.regions,
  },
  getters: {
    passType(state) {
      if (!state.registration.planId) {
        return null;
      }
      return state.passTypes.find(type => type.id === state.registration.planId);
    },
    paymentRequest(state, getters) {
      const { passType, salesTax } = getters;
      if (!passType) {
        return null;
      }
      const paymentRequest = {
        country: 'US',
        currency: 'usd',
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
        shippingOptions: [
          {
            id: 'no_card',
            label: 'Address on file',
            detail: 'Mailing address for your account',
            amount: 0,
          },
        ],
        displayItems: [
          {
            amount: passType.price * 100,
            label: passType.name,
          },
        ],
        total: {
          label: i18n.t('system-name'),
          amount: getters.totalCost * 100,
        },
      };
      if (passType.cardDefault !== null) {
        const cardCost = (passType.cardCost || 0) * 100;
        paymentRequest.shippingOptions = [
          {
            id: 'no_card',
            label: i18n.t('payment-request.no-card.label'),
            detail: i18n.t('payment-request.no-card.detail'),
            amount: 0,
          },
          {
            id: 'fullfill_card',
            label: i18n.t('payment-request.fulfill-card.label'),
            detail: i18n.t('payment-request.fulfill-card.detail'),
            amount: cardCost,
          },
        ];
        if (state.registration.fullfillCard || passType.cardDefault) {
          paymentRequest.shippingOptions.reverse();
        }
        if (state.registration.fullfillCard) {
          paymentRequest.displayItems.push({
            amount: cardCost,
            label: i18n.t('payment-request.card'),
          });
        }
      }
      if (state.promotionCode.code && state.promotionCode.discountedPassCost) {
        const amount = (passType.price - state.promotionCode.discountedPassCost) * -100;
        paymentRequest.displayItems.push({
          amount,
          label: `Promo code ${state.promotionCode.displayCode}`,
        });
      }
      if (passType.salesTax) {
        paymentRequest.displayItems.push({
          amount: salesTax.toFixed(2) * 100,
          label: 'Sales Tax',
        });
      }
      return paymentRequest;
    },
    subTotal(state, getters) {
      const { passType } = getters;

      if (!passType) {
        return null;
      }

      let subtotal = passType.price;
      if (state.promotionCode.code && !Number.isNaN(state.promotionCode.discountedPassCost)) {
        subtotal = state.promotionCode.discountedPassCost;
      }
      if (state.registration.fullfillCard && passType.cardCost) {
        subtotal += passType.cardCost;
      }

      return subtotal;
    },
    salesTax(state, getters) {
      const { subTotal, passType } = getters;

      if (passType.salesTax) {
        return subTotal * passType.salesTax;
      }
      return 0;
    },
    totalCost(state, getters) {
      const { subTotal, salesTax, passType } = getters;

      if (!passType) {
        return null;
      }

      return subTotal + salesTax;
    },
    isValidatingEmail(state) {
      return (
        Object.values(state.emailValidation).length === 0
        || state.emailValidation.loading
        || state.emailValidation.error
      );
    },
    steps(state, getters) {
      const steps = [];
      if (state.passTypes.length > 1) {
        steps.push('pass');
      }
      if (getters.passType && getters.passType.discountVerification) {
        steps.push('discountVerification');
      }
      if (state.enableSms) {
        steps.push('phoneVerification');
      }
      if (state.supportsPayButton) {
        steps.push('payButton');
        steps.push('profileWithoutAccount');
      } else {
        steps.push('profile');
      }
      if (state.requireAddress || (getters.passType && getters.passType.cardDefault !== null)) {
        steps.push('address');
      }
      steps.push('payment');
      steps.push('orderComplete');

      return steps;
    },
    nextStep(state, getters) {
      return (step) => {
        const index = getters.steps.indexOf(step);
        return getters.steps[index + 1];
      };
    },
  },
  actions: {
    updatePass({ state, dispatch }, passTypeId) {
      const passType = state.passTypes.find(type => type.id === passTypeId);
      const newPass = {
        planId: passTypeId,
        discountVerificationNonce: null,
      };
      if (passType.cardDefault === null) {
        newPass.fullfillCard = false;
      }
      if (!passType.allowCash) {
        newPass.paymentMethod = 'credit';
      }
      dispatch('updateRegistration', newPass);

      const promotionCode = state.promotionCode.displayCode || passType.promotionCode;
      if (promotionCode) {
        dispatch('validatePromotionCode', {
          promotionCode,
          retainOnError: true,
        });
      }
      if (state.order.complete) {
        dispatch('updateOrder', { complete: false });
      }
    },
    async requestPhoneVerification({ state, commit, dispatch }, { type, phone, language }) {
      try {
        commit('setPhoneVerficationRequested', { type, phone, sendingCode: true });
        const response = await axios.post(
          state.apiUrl,
          asUrlParams({
            action: 'bts_sms_api',
            bts_sms_api_action: 'request_phone_verification',
            _ajax_nonce: state.nonce,
            type,
            phone,
            language,
          }),
        );

        if (response.data.isVOIP) {
          commit('setPhoneVerficationConfirmed', {
            success: false,
            error: 'VOIP_NOT_ALLOWED',
          });
          return;
        }

        // short circuit the process if verification is not required
        if (!response.data.verificationRequired) {
          if (response.data.isValid) {
            commit('setPhoneVerficationConfirmed', {
              success: true,
              canSkipVerification: true,
            });
            const isLocalNumber = phone.includes('+1');
            dispatch('updateUser', {
              phone,
              optInSms: isLocalNumber,
              allowOptInSms: isLocalNumber,
            });
          } else {
            commit('setPhoneVerficationConfirmed', {
              success: false,
              error: 'INVALID_NUMBER',
            });
          }
          return;
        }

        commit('setPhoneVerficationRequested', {
          type,
          phone,
          codeSent: true,
        });
      } catch {
        commit('setPhoneVerficationRequested', {
          error: 'UNABLE_TO_SEND',
          codeSent: true,
        });
      }
    },
    async verifyPhoneNumber({ state, commit, dispatch }, { code, phone }) {
      try {
        const cancelSource = axios.CancelToken.source();
        commit('setPhoneVerficationConfirmed', {
          code,
          phone,
          loading: true,
          cancelSource,
        });
        const response = await axios.post(
          state.apiUrl,
          asUrlParams({
            action: 'bts_sms_api',
            bts_sms_api_action: 'submit_phone_verification',
            _ajax_nonce: state.nonce,
            code,
            phone,
          }),
          {
            cancelToken: cancelSource.token,
          },
        );
        const id = response.data.phoneVerificationId;
        const success = typeof id === 'string';
        const error = success ? null : 'INVALID_CODE';
        commit('setPhoneVerficationConfirmed', {
          id,
          success,
          code,
          error,
        });
        if (success) {
          const isLocalNumber = phone.includes('+1');
          dispatch('updateUser', {
            phone,
            optInSms: isLocalNumber,
            allowOptInSms: isLocalNumber,
          });
        }
      } catch (error) {
        if (error instanceof axios.Cancel) {
          commit('setPhoneVerficationConfirmed', {});
          return;
        }
        console.warn(error);
        commit('setPhoneVerficationConfirmed', {
          success: false,
          error: 'UNABLE_TO_VALIDATE',
        });
      }
    },
    cancelPhoneNumberVerification(
      { state, commit },
      { cancelRequested } = { cancelRequested: false },
    ) {
      if (state.phoneVerification.cancelSource) {
        state.phoneVerification.cancelSource.cancel();
      }
      commit('setPhoneVerficationConfirmed', {});
      if (cancelRequested) {
        commit('setPhoneVerficationRequested', {});
      }
    },
    async validatePromotionCode({ state, getters, dispatch }, { promotionCode, retainOnError }) {
      dispatch('updatePromotionCode', { loading: true });
      try {
        const response = await axios.post(
          state.apiUrl,
          asUrlParams({
            action: 'bcycle_api',
            bcycle_api_action: 'validate_promocode',
            _ajax_nonce: state.nonce,
            promocode: promotionCode,
            email: state.user.email,
            subscriptionTypeId: state.registration.planId,
          }),
        );
        const value = { success: response.data.success, ...response.data.data };
        value.discountedPassCost = parseFloat(value.discountedPassCost);
        if (getters.passType.salesTax) {
          value.discountedPassCost = Math.round((value.discountedPassCost * 100) / (1 + getters.passType.salesTax)) / 100;
        }
        dispatch('updatePromotionCode', value);
      } catch {
        const value = {
          success: false,
          message: 'Unable to validate promo code at this time.',
        };
        if (retainOnError) {
          value.displayCode = promotionCode;
        }
        dispatch('updatePromotionCode', value);
      }
    },
    async validateDiscount(
      { state, getters, dispatch },
      {
        validationType, validationString, email = null,
      },
    ) {
      try {
        const response = await axios.post(
          state.apiUrl,
          asUrlParams({
            action: 'bcycle_api',
            bcycle_api_action: 'discount_validation',
            validation_type: validationType,
            _ajax_nonce: state.nonce,
            validation_string: validationString,
            email,
            discount_id: getters.passType.discountVerification,
          }),
        );
        if (response.data.is_valid) {
          dispatch('updateRegistration', {
            accessCard: validationType !== 'passcode' ? validationString : null,
            tapCard:
              validationType === 'tap_card'
                ? `${validationString}:${response.data.rider_class}`
                : null,
            rfidCard: validationType === 'rtc_rfid' ? validationString.split(':')[0] : null,
            enrollmentCode: validationType === 'passcode' ? validationString : null,
            discountVerificationNonce: response.data.registration_nonce,
          });
        }
        return response.data;
      } catch (e) {
        console.warn(e);
        if (validationType === 'access_card') {
          return {
            is_valid: false,
            message:
              'An error occurred validating your ACCESS card. Please try again or contact Customer Service for assistance.',
          };
        }
        return {
          is_valid: false,
          message:
            'An error occurred validating your eligibility. Please try again or contact Customer Service for assistance.',
        };
      }
    },
    cancelEmailValidation({ state, commit }) {
      if (state.emailValidation.cancelSource) {
        state.emailValidation.cancelSource.cancel();
      }
      commit('setEmailValidation', {});
    },
    async validateEmail({ state, commit, dispatch }, email) {
      const cancelSource = axios.CancelToken.source();
      commit('setEmailValidation', { loading: true, cancelSource });

      if (!EmailValidator.validate(email)) {
        dispatch('cancelEmailValidation');
        return;
      }

      if (state.emailValidation.email === email) {
        return;
      }

      commit('setEmailValidation', { ...state.emailValidation, email });

      if (state.registration.discountEligibilityType === 'student'
        && !ALLOWED_STUDENT_EMAIL_DOMAIN_REGEX.test(email.toLowerCase())) {
        commit('setEmailValidation', { ...state.emailValidation, loading: false, error: 'Invalid student email' });
      }

      try {
        const response = await axios.post(
          state.apiUrl,
          asUrlParams({
            action: 'bcycle_api',
            bcycle_api_action: 'validate_username',
            _ajax_nonce: state.nonce,
            emailaddress: email,
          }),
          {
            cancelToken: cancelSource.token,
          },
        );
        const value = { success: response.data.success, ...response.data.data };
        commit('setEmailValidation', { ...state.emailValidation, ...value, loading: false });
        Mailcheck.run({
          email,
          suggested: (suggestion) => {
            commit('setEmailValidation', { ...state.emailValidation, suggestion });
          },
          empty: () => {
            commit('setEmailValidation', { ...state.emailValidation, suggestion: null });
          },
        });
      } catch (error) {
        if (error instanceof axios.Cancel) {
          return;
        }
        console.warn(error);
        commit('setEmailValidation', {
          success: false,
          error: 'UNABLE_TO_VALIDATE',
        });
      }
    },
    async submitRegistration({ state, dispatch, getters }) {
      const {
        registration, promotionCode, apiUrl, phoneVerification,
      } = state;
      const user = JSON.parse(JSON.stringify(state.user));
      let orderStatus = {};
      // remove whitespace from zipcode
      user.zipCode = user.zipCode.replace(/\s/g, '');
      // if the user didn't add a zipcode, try to read the zipcode from their credit card
      if (user.zipCode === '' && registration.stripeToken.card) {
        if (
          registration.stripeToken.card.address_zip !== null
          && !registration.stripeToken.card.address_zip.match(/[A-Za-z]/)
        ) {
          user.zipCode = registration.stripeToken.card.address_zip;
          // if the card is international and not matching bcycle's formatting requirements, set a dummy value
        } else if (registration.stripeToken.card.country !== 'US') {
          user.zipCode = '11111';
        }
      }

      // check for payment button international users and make sure bcycle will accept their zipcode
      if ('country' in user && user.country !== 'US' && (user.zipCode.match(/[A-Za-z]/) || !user.zipCode)) {
        // TODO: look into converting payment button country to countryId for BCycle
        user.zipCode = '11111';
      }

      const paymentProfile = {
        paymentProcessorType: 'Stripe',
        tokenId: registration.stripeToken.id,
        paymentCollectionType: 'Website',
      };

      const registrationUser = {
        firstName: user.firstName,
        lastName: user.lastName,
        password: user.password,
        contactEmail: user.email,
        contactPhone: user.phone,
        addressZipCode: user.zipCode,
        optInEmail: user.optInEmail,
        optInSms: user.optInSms,
        visibleOnLeaderBoard: false,
      };

      if (user.addressStreet) {
        registrationUser.addressStreet = user.addressStreet;
        registrationUser.addressCity = user.addressCity;
        registrationUser.addressState = user.addressState;
        registrationUser.countryId = user.countryId || 236;

        if (user.addressStreet2) {
          registrationUser.addressStreet2 = user.addressStreet2;
        }
      }

      const request = {
        type: 'New',
        purchaseSource: 'PartnerRegistration',
        subscriptionTypeId: registration.planId,
        promotionCode: promotionCode.code || '',
        isAutoRenewing: getters.passType.isAutoRenewing,
        fullfillCard: registration.fullfillCard,
        user: registrationUser,
        paymentProfile,
        region: user.region,
      };

      if (registration.tapCard) {
        request.visibleCardNumber = registration.tapCard;
        request.cardClassGroup = 'TAP';
      }

      try {
        dispatch('updateOrder', { loading: true });
        const params = {
          action: 'bcycle_api',
          bcycle_api_action: 'register_stripe_user',
          _ajax_nonce: state.nonce,
          request: encodeURIComponent(JSON.stringify(request)),
          access_card_number: registration.accessCard,
          enrollment_code: registration.enrollmentCode,
          phone_verification_id: phoneVerification.confirmed.id,
          phone_verification_code: phoneVerification.confirmed.code,
        };

        if (typeof getters.passType.discountVerification !== 'undefined') {
          params.discount_id = getters.passType.discountVerification;
          params.discount_nonce = registration.discountVerificationNonce;
          params.discountEligibilityType = registration.discountEligibilityType;
        }
        if (registration.accessCard !== null) {
          params.access_card_number = registration.accessCard;
        }
        if (registration.tapCard || registration.rfidCard) {
          params.visible_card_number = registration.tapCard || registration.rfidCard;
        }
        const response = await axios.post(apiUrl, asUrlParams(params));
        orderStatus = {
          complete: typeof response.data.cost !== 'undefined',
          request: JSON.parse(JSON.stringify(request)),
          promotionCode: JSON.parse(JSON.stringify(promotionCode)),
          ...response.data,
        };
        if (orderStatus.errorList) {
          orderStatus.errorList = orderStatus.errorList.map(item => item.errorCode);
        }
      } catch (error) {
        console.error('registration failed', error);
        if (Vue.$honeybadger) {
          Vue.$honeybadger.notify(error);
        }
        orderStatus = {
          complete: false,
          errorList: ['RequestError'],
        };
      }
      // console.log(orderStatus);
      dispatch('updateOrder', orderStatus);

      if (orderStatus.complete) {
        // log to facebook pixel
        if (window.fbq) {
          const contents = [
            {
              id: orderStatus.request.subscriptionTypeId,
              quantity: 1,
            },
          ];
          // if there is a promo code, log that as well
          if (promotionCode.code) {
            contents.push({
              id: promotionCode.code,
              quantity: 1,
            });
          }
          window.fbq('track', 'Purchase', {
            value: orderStatus.cost,
            currency: 'USD',
            contents,
          });
        }
        // send registration to apps
        dispatch('sendAppMessage', {
          action: 'signup',
          parameters: {
            username: registrationUser.contactEmail,
            password: registrationUser.password,
          },
        });
        // reset registration data
        dispatch('updateRegistration', JSON.parse(JSON.stringify(DEFAULT_REGISTRATION)));
        dispatch('updateUser', JSON.parse(JSON.stringify(DEFAULT_USER)));
        dispatch('cancelEmailValidation');
        dispatch('updatePromotionCode', {});
      }

      return orderStatus;
    },
    updateRegistration({ commit }, registration) {
      Object.entries(registration).forEach(([key, value]) => {
        commit('setRegistration', { key, value });
      });
    },
    updateUser({ commit }, user) {
      Object.entries(user).forEach(([key, value]) => {
        commit('setUser', { key, value });
      });
    },
    updateOrder({ commit }, order) {
      commit('setOrder', order);
    },
    updatePromotionCode({ commit }, promoCode) {
      commit('setPromotionCode', promoCode);
    },
    testPayButtonSupport({ state, commit }) {
      // eslint-disable-next-line
      const paymentRequest = Stripe(state.stripeKey).paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 100,
        },
      });
      // commit('setItem', { key: 'supportsPayButton', value: true });
      paymentRequest
        .canMakePayment()
        .then(response => commit('setItem', { key: 'supportsPayButton', value: response !== null }));
    },
    sendAppMessage(store, { action, parameters }) {
      try {
        if (
          window.webkit
        && window.webkit.messageHandlers
        && window.webkit.messageHandlers.appMessageHandler
        ) {
          const message = {};
          message[action] = parameters;
          window.webkit.messageHandlers.appMessageHandler.postMessage(
            JSON.stringify({
              data: message,
            }),
          );
        }
        if (window.AndroidAppMessage && action in window.AndroidAppMessage) {
          window.AndroidAppMessage[action](...Object.values(parameters));
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
  mutations: {
    setRegistration(state, { key, value }) {
      Vue.set(state.registration, key, value);
    },
    setUser(state, { key, value }) {
      Vue.set(state.user, key, value);
    },
    setPromotionCode(state, promotionCode) {
      state.promotionCode = promotionCode;
    },
    setEmailValidation(state, value) {
      state.emailValidation = value;
    },
    setOrder(state, order) {
      state.order = order;
    },
    setPhoneVerficationRequested(state, value) {
      state.phoneVerification.requested = value;
    },
    setPhoneVerficationConfirmed(state, value) {
      state.phoneVerification.confirmed = value;
    },
    setItem(state, { key, value }) {
      Vue.set(state, key, value);
    },
  },
});
